import React from 'react'
import { DEVICE } from 'theme'
import { useIsMaxDevice } from 'utils/hooks/useMedia'
import { Box } from 'components/Layout'
import Button from 'components/Button'
import Icon from 'components/Icon'
import Datalist from 'components/Datalist'
import Card from 'components/Card'
import TextInput from 'components/TextInput'
import { useToaster } from 'utils/useToaster'
import {
  FIELD_DIVIDER_GUTTER,
  COMMON_BOTTOM_CONTENT_HEIGHT,
  CommonProps as Props,
  ADD_BUTTON_WIDTH,
} from '../common'
import { SectionTitle } from './SectionTitle'
import { useDriveThroughTest, TestsFieldName, DriveThroughFieldName } from '../useDriveThroughForms'

const EXISTING_ENTRY_MESSAGE = 'Test with same name already exist'

export const Tests: React.VFC<Props> = ({ driveThroughForm }) => {
  const { setToastMessage } = useToaster()
  const isTabletOrMobile = useIsMaxDevice(DEVICE.DESKTOP)
  const { values, setFieldValue } = driveThroughForm

  const {
    values: testValues,
    handleSubmit: handleAddNewTest,
    handleChange: handleChangeTestValue,
    errors: testErrors,
    touched: testTouched,
    resetForm: resetTestForm,
  } = useDriveThroughTest({
    onSubmit: () => {
      if (values.tests.some(({ name }) => name === testValues.testName)) {
        setToastMessage(EXISTING_ENTRY_MESSAGE, 'danger')
      } else {
        void setFieldValue(DriveThroughFieldName.TESTS, [
          ...values.tests,
          { name: testValues.testName, price: testValues.testPrice },
        ])
      }
      void resetTestForm()
    },
  })

  const handleRemoveTest = React.useCallback(
    (testName: string) => {
      setFieldValue(
        DriveThroughFieldName.TESTS,
        values.tests.filter(({ name }) => name !== testName)
      )
    },
    [setFieldValue, values.tests]
  )

  return (
    <Box pb={COMMON_BOTTOM_CONTENT_HEIGHT}>
      <SectionTitle isTabletOrMobile={isTabletOrMobile}>Tests</SectionTitle>
      <Box
        display={!isTabletOrMobile ? 'flex' : undefined}
        justifyContent="space-between"
        alignItems="center"
      >
        <Box
          display="grid"
          gridTemplateColumns={!isTabletOrMobile ? '2fr 1fr' : undefined}
          gridGap={FIELD_DIVIDER_GUTTER}
        >
          <TextInput
            name={TestsFieldName.TEST_NAME}
            isFullWidth
            label="Name"
            value={testValues.testName}
            onChange={handleChangeTestValue}
            errorMessage={
              testErrors.testName && testTouched.testName ? testErrors.testName : undefined
            }
          />
          <TextInput
            name={TestsFieldName.TEST_PRICE}
            type="number"
            isFullWidth
            label="Price"
            value={testValues.testPrice}
            onChange={handleChangeTestValue}
            errorMessage={
              testErrors.testPrice && testTouched.testPrice ? testErrors.testPrice : undefined
            }
          />
        </Box>
        <Button
          onClick={() => handleAddNewTest()}
          mb={isTabletOrMobile ? COMMON_BOTTOM_CONTENT_HEIGHT : '0'}
          width={isTabletOrMobile ? '100%' : ADD_BUTTON_WIDTH}
          leftIcon={<Icon.Plus />}
        >
          Add
        </Button>
      </Box>
      {values.tests.length > 0 && (
        <Card>
          <Datalist
            data={values.tests}
            renderTableHeader={() => (
              <Datalist.Row
                display="grid"
                gridTemplateColumns={isTabletOrMobile ? '3fr 1fr 0.75fr' : 'repeat(3, 1fr)'}
              >
                <Datalist.HeaderCell label="name" />
                <Datalist.HeaderCell label="price" />
                <Datalist.HeaderCell label="action" />
              </Datalist.Row>
            )}
            shouldRenderTableHeader
            renderTableRow={({ name, price }) => (
              <Datalist.Row
                display="grid"
                gridTemplateColumns={isTabletOrMobile ? '3fr 1fr 0.75fr' : 'repeat(3, 1fr)'}
              >
                <Datalist.Cell label="name">{name}</Datalist.Cell>
                <Datalist.Cell label="price">${price}</Datalist.Cell>
                <Datalist.Cell label="action">
                  <Button
                    size="small"
                    appearance="link"
                    colorVariant="danger"
                    onClick={() => handleRemoveTest(name)}
                  >
                    <Icon.Trash />
                  </Button>
                </Datalist.Cell>
              </Datalist.Row>
            )}
          />
        </Card>
      )}
    </Box>
  )
}
